import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Ban,
  Check,
  CheckCircle2,
  Copy,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Rocket,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import {
  activateWhiteLabelDeployment,
  buildCustomDomainLoginUrl,
  buildCustomDomainWebsiteUrl,
  buildGeneratedTenantDomain,
  buildGeneratedTenantWebsiteUrl,
  buildLocalTenantWebsiteUrl,
  buildProductionTenantWebsiteUrl,
  buildTenantLoginUrl,
  connectWhiteLabelDomain,
  createWhiteLabelDeployment,
  deleteWhiteLabelDeployment,
  disconnectWhiteLabelDomain,
  fetchWhiteLabelDeployments,
  fetchWhiteLabelProposalPdf,
  reorderWhiteLabelDeployments,
  isLocalDevelopmentBuild,
  isReservedTenantSlug,
  publishWhiteLabelDeployment,
  resolveEffectiveTenantLoginUrl,
  resolveEffectiveTenantWebsite,
  resetTenantOnboarding,
  setWhiteLabelDeploymentStatus,
  suspendWhiteLabelDeployment,
  updateWhiteLabelDeployment,
  uploadWhiteLabelLogo,
  verifyWhiteLabelDomain,
  WHITE_LABEL_WORDING_TOKEN_KEYS,
  type WhiteLabelConfig,
  type WhiteLabelConfigInput,
  type WhiteLabelCounts,
  type WhiteLabelDomainStatus,
  type WhiteLabelInfrastructureState,
  type WhiteLabelLaunchControls,
  type WhiteLabelPublishResult,
  type WhiteLabelStatus,
} from '../../../lib/whiteLabel';
import AdminTablePagination from '../../ui/AdminTablePagination';
import { withAssetVersion } from '../../../lib/whiteLabelBranding';
import { cacheLogoAccentColors, extractLogoAccentColors } from '../../../lib/logoColors';
import { getTextLogoInitials, getTextLogoName } from '../../../lib/textLogo';
import { getAppLogoSrc } from '../../../constants/branding';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import ConfirmationModal from '../../ui/ConfirmationModal';

type WhiteLabelDeploymentsSectionProps = {
  theme: 'light' | 'dark';
  onNotify?: (message: string, kind?: 'success' | 'error') => void;
};

type WhiteLabelFormState = {
  companyName: string;
  productName: string;
  shortProductName: string;
  tenantSlug: string;
  supportEmail: string;
  legalEntityName: string;
  primaryLogoUrl: string;
  primaryLogoLightUrl: string;
  sidebarLogoUrl: string;
  sidebarLogoLightUrl: string;
  compactLogoUrl: string;
  compactLogoLightUrl: string;
  loadingPageLogoUrl: string;
  loadingPageLogoSize: 'small' | 'medium' | 'large' | 'x-large';
  loadingPageLogoGlow: boolean;
  useTextLogo: boolean;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  partnerRevenueShare: string;
  partnerMonthlyPrice: string;
  proposalFitSummary: string;
  outreachFitSentence: string;
  customDomain: string;
  domainStatus: WhiteLabelDomainStatus;
  status: WhiteLabelStatus;
  wording: Record<string, string>;
  launchControls: WhiteLabelLaunchControls;
  optionsRelevant: boolean;
  addToFoundingPartnerFunnel: boolean;
};

/**
 * Canonical registry of branding URL fields. This is the single source of truth
 * for every brand asset the form can persist. Adding a new branding asset means
 * adding one entry here, and it will automatically flow into the empty form
 * initializer, the edit-time loader, and the save payload builder.
 */
const BRANDING_URL_FIELDS = [
  'primaryLogoUrl',
  'primaryLogoLightUrl',
  'sidebarLogoUrl',
  'sidebarLogoLightUrl',
  'compactLogoUrl',
  'compactLogoLightUrl',
  'loadingPageLogoUrl',
  'faviconUrl',
] as const;

type BrandingUrlField = (typeof BRANDING_URL_FIELDS)[number];

const EMPTY_BRANDING: Record<BrandingUrlField, string> = BRANDING_URL_FIELDS.reduce(
  (acc, field) => {
    acc[field] = '';
    return acc;
  },
  {} as Record<BrandingUrlField, string>,
);

const EMPTY_FORM: WhiteLabelFormState = {
  companyName: '',
  productName: '',
  shortProductName: '',
  tenantSlug: '',
  supportEmail: '',
  legalEntityName: '',
  ...EMPTY_BRANDING,
  loadingPageLogoSize: 'medium',
  loadingPageLogoGlow: false,
  useTextLogo: true,
  primaryColor: '#2563eb',
  secondaryColor: '',
  partnerRevenueShare: '35',
  partnerMonthlyPrice: '45',
  proposalFitSummary: '',
  outreachFitSentence: '',
  customDomain: '',
  domainStatus: 'unverified',
  status: 'draft',
  wording: {},
  launchControls: {
    stocks: true,
    futures: false,
    forex: false,
    crypto: false,
    replayChart: true,
    navigationLayout: 'both',
    themeMode: 'light_dark',
  },
  addToFoundingPartnerFunnel: true,
};

const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml']);
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2048;

/**
 * Cache-bust a branding asset URL for the admin previews using the same version
 * (config updatedAt) that the rest of the app uses. Because updatedAt changes on
 * every save, a freshly saved image is fetched fresh instead of reusing stale
 * browser-cached bytes for the same URL.
 */
const versionedBrandingUrl = (url: string | null | undefined, updatedAt?: string | null): string | null =>
  withAssetVersion(url ?? null, updatedAt);

/** Parse a partner revenue share % string to a finite 0-100 number, defaulting to 35. */
const toFinitePercent = (value: string): number => {
  const num = Number((value ?? '').trim());
  if (!Number.isFinite(num) || num < 0 || num > 100) return 35;
  return Math.round(num * 100) / 100;
};

/** Parse a non-negative decimal price (USD), defaulting to 45. No upper clamp. */
const toFiniteMoney = (value: string): number => {
  const num = Number((value ?? '').trim());
  if (!Number.isFinite(num) || num < 0) return 45;
  return Math.round(num * 100) / 100;
};

const proposalMissingFields = (config: WhiteLabelConfig): string[] => {
  const missing: string[] = [];
  if (!String(config.companyName || '').trim()) missing.push('Partner Name');
  if (!Number.isFinite(Number(config.partnerRevenueShare))) missing.push('Partner Share');
  if (!Number.isFinite(Number(config.partnerMonthlyPrice))) missing.push('Subscription Price');
  if (!String(config.proposalFitSummary || '').trim()) missing.push('Proposal Fit Summary');
  if (config.status !== 'active' || !resolveEffectiveTenantWebsite(config)) missing.push('Active Website');
  return missing;
};

// Established platform default support inbox (see src/config/branding/default.ts and the
// server contact-routing destinations). The tenant form no longer exposes an editable
// Support email field, so new tenants inherit this value; the edit flow preserves the
// stored tenant value instead of overwriting it.
const DEFAULT_SUPPORT_EMAIL = 'support@trade-proof.com';

const WORDING_LABELS: Record<string, string> = {
  loginHeading: 'Login Heading',
  loginDescription: 'Login Description',
  signupHeading: 'Signup Heading',
  signupDescription: 'Signup Description',
  dashboardWelcome: 'Welcome Message',
  footerCopyright: 'Footer Copyright',
};

function customDomainStatusLabel(config: { customDomain?: string | null; domainStatus?: WhiteLabelDomainStatus }): string {
  if (!config.customDomain) return 'Not Configured';
  if (config.domainStatus === 'verified') return 'Verified';
  if (config.domainStatus === 'pending') return 'Waiting for DNS';
  if (config.domainStatus === 'failed') return 'Error';
  return 'Not Configured';
}

function normalizeSlug(value: string) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '');
}

function openWebsiteInNewTab(url: string) {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch {
      return false;
    }
  }
}

function buildDefaultWording(form: WhiteLabelFormState) {
  // Company Name is the primary display name across the application. Product name and
  // short product name are derived from it on save for backward compatibility with the
  // stored schema (the server still stores/requires product_name).
  const companyName = String(form.companyName || form.productName || '').trim();
  const productName = String(form.productName || companyName || '').trim();
  const legalEntityName = String(form.legalEntityName || companyName).trim();
  // Support email is no longer a visible input. Preserve the stored value on edit and
  // fall back to the established platform default for new tenants.
  const supportEmail = String(form.supportEmail || DEFAULT_SUPPORT_EMAIL || '').trim().toLowerCase();
  const currentYear = new Date().getFullYear();

  return {
    companyName,
    productName,
    shortProductName: String(form.shortProductName || companyName).trim(),
    supportEmail,
    legalEntityName,
    loginHeading: companyName ? `Welcome back to ${companyName}` : 'Welcome back',
    loginDescription: companyName ? `Sign in to your ${companyName} account to continue.` : 'Sign in to your account to continue.',
    signupHeading: companyName ? `Create your ${companyName} account` : 'Create your account',
    signupDescription: companyName ? `Start tracking your trading performance with ${companyName}.` : 'Start tracking your trading performance.',
    dashboardWelcome: companyName ? `Welcome to ${companyName}` : 'Welcome',
    footerCopyright: `© ${currentYear} ${legalEntityName || companyName || productName || 'Trade-Proof'}. All rights reserved.`,
  };
}

function formFromConfig(config: WhiteLabelConfig | null): WhiteLabelFormState {
  if (!config) return { ...EMPTY_FORM, wording: {} };

  return {
    companyName: config.companyName,
    productName: config.productName,
    shortProductName: config.shortProductName || '',
    tenantSlug: config.tenantSlug,
    supportEmail: config.supportEmail,
    legalEntityName: config.legalEntityName || '',
    loadingPageLogoSize: config.loadingPageLogoSize || 'medium',
    loadingPageLogoGlow: config.loadingPageLogoGlow !== false,
    useTextLogo: config.useTextLogo === true,
    ...BRANDING_URL_FIELDS.reduce((acc, field) => {
      // Every branding asset URL is loaded generically so new assets auto-join edit.
      acc[field] = String(config[field as keyof WhiteLabelConfig] || '').trim();
      return acc;
    }, {} as Record<BrandingUrlField, string>),
    primaryColor: config.primaryColor || '#2563eb',
    secondaryColor: config.secondaryColor || '',
    partnerRevenueShare: config.partnerRevenueShare != null ? String(config.partnerRevenueShare) : '35',
    partnerMonthlyPrice: config.partnerMonthlyPrice != null ? String(config.partnerMonthlyPrice) : '45',
    proposalFitSummary: config.proposalFitSummary || '',
    outreachFitSentence: config.outreachFitSentence || '',
    customDomain: config.customDomain || '',
    domainStatus: config.domainStatus,
    status: config.status,
    wording: { ...config.wording, supportEmail: config.supportEmail },
    launchControls: {
      stocks: config.launchControls?.stocks !== false,
      futures: config.launchControls?.futures === true,
      forex: config.launchControls?.forex === true,
      crypto: config.launchControls?.crypto === true,
      replayChart: config.launchControls?.replayChart !== false,
      navigationLayout: config.launchControls?.navigationLayout || 'both',
      themeMode: config.launchControls?.themeMode || 'light_dark',
    },
    optionsRelevant: config.optionsRelevant === true,
  };
}

function validateForm(form: WhiteLabelFormState): Record<string, string> {
  const errors: Record<string, string> = {};

  // Product name and short product name are no longer visible inputs. They are derived
  // (productName = shortProductName = companyName) on save so the stored schema keeps
  // working, and support email uses the platform default. Company name and tenant slug
  // stay required.
  if (!String(form.companyName || '').trim()) errors.companyName = 'Company name is required.';

  const slug = normalizeSlug(form.tenantSlug);
  if (!slug) errors.tenantSlug = 'Tenant slug is required.';
  else if (slug.length < 3 || !/^[a-z0-9](?:[a-z0-9-]{1,56}[a-z0-9])?$/.test(slug)) {
    errors.tenantSlug = 'Tenant slug must be 3-58 characters using lowercase letters, numbers, and hyphens.';
  } else if (isReservedTenantSlug(slug)) {
    errors.tenantSlug = 'This tenant slug is reserved for platform infrastructure.';
  }

  if (form.primaryColor && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.primaryColor)) {
    errors.primaryColor = 'Primary color must be a hex color like #2563EB.';
  }
  if (form.secondaryColor && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.secondaryColor)) {
    errors.secondaryColor = 'Secondary color must be a hex color like #0EA5E9.';
  }

  const share = Number(form.partnerRevenueShare);
  if (form.partnerRevenueShare.trim() === '' || !Number.isFinite(share) || share < 0 || share > 100) {
    errors.partnerRevenueShare = 'Partner revenue share must be a number between 0 and 100.';
  }

  const price = Number(form.partnerMonthlyPrice);
  if (form.partnerMonthlyPrice.trim() === '' || !Number.isFinite(price) || price < 0) {
    errors.partnerMonthlyPrice = 'Partner monthly price must be a non-negative number.';
  }

  return errors;
}

function computeCounts(configs: WhiteLabelConfig[]): WhiteLabelCounts {
  const counts: WhiteLabelCounts = { active: 0, draft: 0, suspended: 0, total: configs.length };
  for (const config of configs) {
    if (config.status === 'active') counts.active += 1;
    else if (config.status === 'draft') counts.draft += 1;
    else if (config.status === 'suspended') counts.suspended += 1;
  }
  return counts;
}

function getActiveAssetAbbreviations(config: WhiteLabelConfig): Array<{ code: string }> {
  const controls = config.launchControls || {};
  const assets = [
    controls.stocks !== false ? { code: 'ST' } : null,
    controls.futures === true ? { code: 'FU' } : null,
    controls.forex === true ? { code: 'FO' } : null,
    controls.crypto === true ? { code: 'CR' } : null,
  ].filter((asset): asset is { code: string } => Boolean(asset));

  return config.optionsRelevant === true ? [...assets, { code: 'OP' }] : assets;
}

function validateImageFile(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const type = String(file.type || '').toLowerCase();
    if (!ALLOWED_IMAGE_TYPES.has(type)) {
      resolve('Unsupported image format. Use PNG, JPG, JPEG, WEBP, or SVG.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      resolve('Logo file exceeds the 5 MB size limit.');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        URL.revokeObjectURL(objectUrl);
        resolve(`Image dimensions (${width}×${height}) exceed the 2048px limit.`);
        return;
      }
      if (type === 'image/png' && width > 0 && height > 0) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (context) {
            context.drawImage(image, 0, 0);
            const data = context.getImageData(0, 0, width, height).data;
            let hasTransparency = false;
            for (let index = 3; index < data.length; index += 4) {
              if (data[index] < 255) {
                hasTransparency = true;
                break;
              }
            }
            if (!hasTransparency) {
              URL.revokeObjectURL(objectUrl);
              resolve('PNG logo has no transparent background. Upload a PNG with transparency, or use JPG/WEBP.');
              return;
            }
          }
        } catch {
          // Canvas pixel reads can be blocked in some environments; transparency check is advisory.
        }
      }
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve('The selected file could not be read as an image.');
    };
    image.src = objectUrl;
  });
}

export default function WhiteLabelDeploymentsSection({ theme, onNotify }: WhiteLabelDeploymentsSectionProps) {
  const isDark = theme === 'dark';

  const [configs, setConfigs] = useState<WhiteLabelConfig[]>([]);
  const [counts, setCounts] = useState<WhiteLabelCounts>({ active: 0, draft: 0, suspended: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subscriberSort, setSubscriberSort] = useState<'asc' | 'desc' | null>(null);
  const [tenantPage, setTenantPage] = useState(1);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<WhiteLabelConfig | null>(null);
  const [form, setForm] = useState<WhiteLabelFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [removingField, setRemovingField] = useState<BrandingUrlField | null>(null);
  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);
  const [confirmDeleteConfig, setConfirmDeleteConfig] = useState<WhiteLabelConfig | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmTenantOnboardingReset, setConfirmTenantOnboardingReset] = useState<WhiteLabelConfig | null>(null);
  const [tenantOnboardingResetting, setTenantOnboardingResetting] = useState(false);
  const [infrastructure, setInfrastructure] = useState<WhiteLabelInfrastructureState | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<WhiteLabelPublishResult | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [customDomainDraft, setCustomDomainDraft] = useState('');
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainNotice, setDomainNotice] = useState<string | null>(null);
  const [proposalConfig, setProposalConfig] = useState<WhiteLabelConfig | null>(null);
  const [proposalLoading, setProposalLoading] = useState<'open' | 'download' | null>(null);
  const [proposalSaving, setProposalSaving] = useState(false);
  const [proposalEditing, setProposalEditing] = useState(false);
  const [proposalSummaryDraft, setProposalSummaryDraft] = useState('');
  const [outreachFitSentenceDraft, setOutreachFitSentenceDraft] = useState('');
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [tenantsExpanded, setTenantsExpanded] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await fetchWhiteLabelDeployments();
      setConfigs(result.configs);
      setCounts(result.counts);
      setInfrastructure(result.infrastructure || null);
    } catch (error) {
      setLoadError(String((error as Error)?.message || 'Unable to load white-label deployments.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const notify = useCallback(
    (message: string, kind?: 'success' | 'error') => {
      onNotify?.(message, kind);
    },
    [onNotify],
  );

  const openEdit = useCallback(
    (config: WhiteLabelConfig) => {
      setEditingConfig(config);
      setForm(formFromConfig(config));
      setFormErrors({});
      setFormError(null);
      setUploadError(null);
      setCustomDomainDraft(String(config.customDomain || ''));
      setDomainError(null);
      setDomainNotice(null);
      setModalOpen(true);
    },
    [],
  );

  const updateFormField = useCallback((field: keyof WhiteLabelFormState, value: string | boolean) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    // Clear the field's inline error once the value changes.
    setFormErrors((previous) => {
      if (!previous[field]) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const updateLaunchControl = useCallback((field: keyof WhiteLabelLaunchControls, value: boolean | 'both' | 'side_only' | 'top_only') => {
    setForm((previous) => ({
      ...previous,
      launchControls: { ...previous.launchControls, [field]: value },
    }));
  }, []);

  const updateWordingToken = useCallback((key: string, value: string) => {
    setForm((previous) => ({ ...previous, wording: { ...previous.wording, [key]: value } }));
  }, []);

  const regenerateWording = useCallback(() => {
    setForm((previous) => ({ ...previous, wording: buildDefaultWording(previous) }));
  }, []);

  const applyServerConfig = useCallback(
    (updated: WhiteLabelConfig | null | undefined) => {
      if (!updated?.id) return;
      setConfigs((previous) => {
        const index = previous.findIndex((config) => config.id === updated.id);
        const next = index >= 0 ? previous.map((config) => (config.id === updated.id ? updated : config)) : [...previous, updated];
        next.sort((a, b) => a.priorityOrder - b.priorityOrder);
        setCounts(computeCounts(next));
        return next;
      });
    },
    [],
  );

  const uploadField = useCallback(
    async (field: 'primaryLogo' | 'primaryLogoLight' | 'sidebarLogo' | 'sidebarLogoLight' | 'compactLogo' | 'compactLogoLight' | 'loadingPageLogo' | 'favicon', file: File) => {
      const tenantSlug = normalizeSlug(form.tenantSlug) || 'draft';
      setUploadingField(field);
      setUploadError(null);
      try {
        const derivedStripeColors = field === 'primaryLogo' ? await extractLogoAccentColors(file) : null;
        const url = await uploadWhiteLabelLogo({ file, tenantSlug, field });
        if (field === 'primaryLogo') cacheLogoAccentColors(url, derivedStripeColors);
        setForm((previous) => ({
          ...previous,
          [field === 'primaryLogo' ? 'primaryLogoUrl'
            : field === 'primaryLogoLight' ? 'primaryLogoLightUrl'
            : field === 'sidebarLogo' ? 'sidebarLogoUrl'
            : field === 'sidebarLogoLight' ? 'sidebarLogoLightUrl'
            : field === 'compactLogo' ? 'compactLogoUrl'
            : field === 'compactLogoLight' ? 'compactLogoLightUrl'
            : field === 'loadingPageLogo' ? 'loadingPageLogoUrl'
            : 'faviconUrl']: url,
        }));
        notify(
          field === 'primaryLogo'
            ? 'Primary logo uploaded.'
            : field === 'primaryLogoLight'
            ? 'Primary logo (light) uploaded.'
            : field === 'sidebarLogo'
            ? 'Sidebar logo uploaded.'
            : field === 'sidebarLogoLight'
            ? 'Sidebar logo (light) uploaded.'
            : field === 'compactLogo'
            ? 'Compact logo uploaded.'
            : field === 'compactLogoLight'
            ? 'Compact logo (light) uploaded.'
            : field === 'loadingPageLogo'
            ? 'Loading page logo uploaded.'
            : 'Favicon uploaded.',
        );
      } catch (error) {
        setUploadError(String((error as Error)?.message || 'Logo upload failed.'));
      } finally {
        setUploadingField(null);
      }
    },
    [form.tenantSlug, notify],
  );

  const handleLogoFileChange = useCallback(
    async (field: 'primaryLogo' | 'primaryLogoLight' | 'sidebarLogo' | 'sidebarLogoLight' | 'compactLogo' | 'compactLogoLight' | 'loadingPageLogo' | 'favicon', file: File | undefined) => {
      if (!file) return;
      const validationError = await validateImageFile(file);
      if (validationError) {
        setUploadError(validationError);
        return;
      }
      void uploadField(field, file);
    },
    [uploadField],
  );

  const removeLocalConfig = useCallback(
    (id: string) => {
      setConfigs((previous) => {
        const next = previous.filter((config) => config.id !== id);
        setCounts(computeCounts(next));
        return next;
      });
    },
    [],
  );

  const buildInput = useCallback((draft: WhiteLabelFormState): WhiteLabelConfigInput => {
    const wording = { ...buildDefaultWording(draft), ...draft.wording };
    return {
      // Company Name is the primary display name. Product name and short product name
      // are derived from it on save so existing tenants/data keep working unchanged.
      companyName: String(draft.companyName || draft.productName || '').trim(),
      productName: String(draft.productName || draft.companyName || '').trim(),
      shortProductName: String(draft.shortProductName || draft.companyName || '').trim(),
      tenantSlug: normalizeSlug(draft.tenantSlug),
      // Support email stays in the stored/API payload. Preserve the stored value on edit
      // and fall back to the established platform default for new tenants.
      supportEmail: String(draft.supportEmail || DEFAULT_SUPPORT_EMAIL || '').trim().toLowerCase(),
      legalEntityName: String(draft.legalEntityName || '').trim(),
      loadingPageLogoSize: draft.loadingPageLogoSize,
      loadingPageLogoGlow: draft.loadingPageLogoGlow,
      useTextLogo: draft.useTextLogo,
      // Every branding asset URL is forwarded generically so adding a future asset
      // only requires one entry in BRANDING_URL_FIELDS.
      ...BRANDING_URL_FIELDS.reduce((acc, field) => {
        acc[field] = String((draft as WhiteLabelFormState)[field] || '').trim();
        return acc;
      }, {} as Partial<Record<BrandingUrlField, string>>),
      primaryColor: String(draft.primaryColor || '').trim(),
      secondaryColor: String(draft.secondaryColor || '').trim(),
      partnerRevenueShare: toFinitePercent(draft.partnerRevenueShare),
      partnerMonthlyPrice: toFiniteMoney(draft.partnerMonthlyPrice),
      proposalFitSummary: String(draft.proposalFitSummary || '').trim(),
      outreachFitSentence: String(draft.outreachFitSentence || '').trim(),
      customDomain: String(draft.customDomain || '').trim().toLowerCase(),
      domainStatus: draft.domainStatus,
      wording,
      launchControls: { ...draft.launchControls },
      optionsRelevant: draft.optionsRelevant,
    };
  }, []);

  const removeLogo = useCallback(async (urlField: BrandingUrlField) => {
    if (!editingConfig) {
      setForm((previous) => ({ ...previous, [urlField]: '' }));
      return;
    }

    setRemovingField(urlField);
    setUploadError(null);
    try {
      const saved = await updateWhiteLabelDeployment(editingConfig.id, buildInput({ ...form, [urlField]: '' }));
      applyServerConfig(saved);
      setForm(formFromConfig(saved));
      notify('Logo removed.');
    } catch (error) {
      setUploadError(String((error as Error)?.message || 'Logo removal failed.'));
    } finally {
      setRemovingField(null);
    }
  }, [applyServerConfig, buildInput, editingConfig, form, notify]);

  const focusAndScrollToField = useCallback((field: string) => {
    const fieldElement = document.querySelector<HTMLElement>(`[data-field="${field}"]`);
    if (fieldElement) {
      if (typeof fieldElement.scrollIntoView === 'function') {
        fieldElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      fieldElement.focus({ preventScroll: true });
    }
  }, []);

  const handleSaveDraft = useCallback(async (options?: { applyStatus?: WhiteLabelStatus }) => {
    const errors = validateForm(form);
    setFormErrors(errors);
    setFormError(null);
    const firstInvalidField = Object.keys(errors)[0];
    if (firstInvalidField) {
      focusAndScrollToField(firstInvalidField);
      return;
    }

    setSaving(true);
    try {
      const input = buildInput(form);
      let creationWarning: string | null = null;
      let saved: WhiteLabelConfig;
      if (editingConfig) {
        saved = await updateWhiteLabelDeployment(editingConfig.id, input);
        if (form.status !== editingConfig.status) {
          saved = await setWhiteLabelDeploymentStatus(saved.id, form.status);
        }
      } else {
        const created = await createWhiteLabelDeployment({ ...input, addToFoundingPartnerFunnel: form.addToFoundingPartnerFunnel });
        saved = created.config;
        creationWarning = created.foundingPartnerWarning || null;
      }

      const desiredStatus = options?.applyStatus;
      if (!editingConfig && desiredStatus && desiredStatus !== 'draft') {
        saved = desiredStatus === 'active'
          ? await activateWhiteLabelDeployment(saved.id)
          : await suspendWhiteLabelDeployment(saved.id);
      }

      applyServerConfig(saved);
      setModalOpen(false);
      if (!editingConfig && form.addToFoundingPartnerFunnel) window.dispatchEvent(new CustomEvent('founding-partner-prospect-created'));
      notify(creationWarning || (editingConfig
        ? 'White-label tenant updated.'
        : desiredStatus && desiredStatus !== 'draft'
          ? `White-label tenant created and ${desiredStatus}.`
        : 'White-label tenant created as a draft.'));
    } catch (error) {
      const code = String((error as { code?: string })?.code || '').trim().toLowerCase();
      const message = String((error as Error)?.message || 'Unable to save the white-label tenant.');
      if (code === 'duplicate_tenant_slug' || /already in use/.test(message)) {
        setFormErrors((previous) => ({ ...previous, tenantSlug: 'This tenant slug is already in use.' }));
      } else if (code === 'domain_collision' || /domain.*already/.test(message)) {
        setFormErrors((previous) => ({ ...previous, customDomain: message }));
      } else {
        setFormError(message);
      }
    } finally {
      setSaving(false);
    }
  }, [applyServerConfig, buildInput, editingConfig, focusAndScrollToField, form, notify]);

  const handleTenantOnboardingReset = useCallback(async () => {
    if (!confirmTenantOnboardingReset || tenantOnboardingResetting) return;
    const config = confirmTenantOnboardingReset;
    setTenantOnboardingResetting(true);
    try {
      const result = await resetTenantOnboarding(config.tenantSlug);
      setConfirmTenantOnboardingReset(null);
      notify(`Onboarding reset for this tenant (${result.affectedUsers} active users).`);
    } catch (error) {
      notify(String((error as Error)?.message || 'Unable to reset onboarding for this tenant.'), 'error');
    } finally {
      setTenantOnboardingResetting(false);
    }
  }, [confirmTenantOnboardingReset, notify, tenantOnboardingResetting]);

  const handleActivate = useCallback(
    async (config: WhiteLabelConfig) => {
      try {
        const updated = await activateWhiteLabelDeployment(config.id);
        applyServerConfig(updated);
        notify(`${updated.companyName || updated.productName} activated.`);
      } catch (error) {
        notify(String((error as Error)?.message || 'Activation failed.'), 'error');
      }
    },
    [applyServerConfig, notify],
  );

  const handlePublish = useCallback(
    async (config: WhiteLabelConfig) => {
      setPublishingId(config.id);
      setPublishError(null);
      try {
        const result = await publishWhiteLabelDeployment(config.id);
        setPublishResult(result);
        applyServerConfig({ ...config, status: 'active', publishedAt: result.publishedAt, publishError: null });
        notify(`${result.tenantSlug} published.`);
      } catch (error) {
        setPublishError(String((error as Error)?.message || 'Publish failed.'));
        notify(String((error as Error)?.message || 'Publish failed.'), 'error');
        void refresh();
      } finally {
        setPublishingId(null);
      }
    },
    [applyServerConfig, notify, refresh],
  );

  const handleProposal = useCallback(async (mode: 'open' | 'download') => {
    if (!proposalConfig || proposalLoading) return;
    const summary = proposalSummaryDraft.trim();
    const needsSave = proposalEditing || !proposalConfig.proposalFitSummary?.trim();
    if (needsSave && (!summary || summary.length > 600)) {
      setProposalError(summary.length > 600
        ? 'Fit Summary is too long for the proposal. Please shorten it.'
        : 'Add a short Proposal Fit Summary before generating the proposal.');
      return;
    }
    const popup = mode === 'open' ? window.open('', '_blank') : null;
    setProposalSaving(needsSave);
    setProposalLoading(mode);
    setProposalError(null);
    try {
      let currentConfig = proposalConfig;
      if (needsSave) {
        currentConfig = await updateWhiteLabelDeployment(proposalConfig.id, {
          ...buildInput(formFromConfig(proposalConfig)),
          proposalFitSummary: summary,
          outreachFitSentence: outreachFitSentenceDraft.trim() || null,
        });
        setProposalConfig(currentConfig);
        setProposalEditing(false);
      }
      setProposalSaving(false);
      const result = await fetchWhiteLabelProposalPdf(currentConfig.id);
      const url = URL.createObjectURL(result.blob);
      if (mode === 'open' && popup) {
        popup.location.href = url;
      } else if (mode === 'download') {
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        link.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setProposalConfig(null);
    } catch (error) {
      popup?.close();
      setProposalError(String((error as Error)?.message || 'Couldn’t generate proposal. Please try again.'));
    } finally {
      setProposalLoading(null);
      setProposalSaving(false);
    }
  }, [buildInput, outreachFitSentenceDraft, proposalConfig, proposalEditing, proposalLoading, proposalSummaryDraft]);

  const openCreate = useCallback(() => {
    setEditingConfig(null);
    setForm({ ...EMPTY_FORM, wording: {} });
    setFormErrors({});
    setFormError(null);
    setCustomDomainDraft('');
    setDomainError(null);
    setDomainNotice(null);
    setModalOpen(true);
  }, []);

  const applyDomainState = useCallback((config: WhiteLabelConfig) => {
    setEditingConfig(config);
    setCustomDomainDraft(String(config.customDomain || ''));
    setForm((previous) => ({
      ...previous,
      customDomain: String(config.customDomain || ''),
      domainStatus: config.domainStatus,
    }));
    applyServerConfig(config);
  }, [applyServerConfig]);

  const handleConnectDomain = useCallback(async () => {
    if (!editingConfig) return;
    setDomainBusy(true);
    setDomainError(null);
    setDomainNotice(null);
    try {
      const updated = await connectWhiteLabelDomain(editingConfig.id, customDomainDraft);
      applyDomainState(updated);
      setDomainNotice(updated.domainStatus === 'verified'
        ? 'Custom domain connected and verified.'
        : 'Custom domain connected. DNS setup is required to verify it.');
      notify(updated.domainStatus === 'verified' ? 'Custom domain connected and verified.' : 'Custom domain connected — DNS setup required.');
    } catch (error) {
      setDomainError(String((error as Error)?.message || 'Unable to connect the custom domain.'));
    } finally {
      setDomainBusy(false);
    }
  }, [applyDomainState, customDomainDraft, editingConfig, notify]);

  const handleVerifyDomain = useCallback(async () => {
    if (!editingConfig) return;
    setDomainBusy(true);
    setDomainError(null);
    setDomainNotice(null);
    try {
      const updated = await verifyWhiteLabelDomain(editingConfig.id);
      applyDomainState(updated);
      setDomainNotice(updated.domainStatus === 'verified'
        ? 'Custom domain verified. SSL/HTTPS will activate on the provider.'
        : 'Verification not complete yet — DNS may still be propagating.');
    } catch (error) {
      setDomainError(String((error as Error)?.message || 'Domain verification check failed.'));
    } finally {
      setDomainBusy(false);
    }
  }, [applyDomainState, editingConfig]);

  const handleDisconnectDomain = useCallback(async () => {
    if (!editingConfig) return;
    setDomainBusy(true);
    setDomainError(null);
    setDomainNotice(null);
    try {
      const updated = await disconnectWhiteLabelDomain(editingConfig.id);
      applyDomainState(updated);
      setDomainNotice('Custom domain disconnected. The generated URL remains the effective website.');
      notify('Custom domain disconnected.');
    } catch (error) {
      setDomainError(String((error as Error)?.message || 'Unable to disconnect the custom domain.'));
    } finally {
      setDomainBusy(false);
    }
  }, [applyDomainState, editingConfig, notify]);

  const handleSuspend = useCallback(
    async (config: WhiteLabelConfig) => {
      try {
        const updated = await suspendWhiteLabelDeployment(config.id);
        applyServerConfig(updated);
        notify(`${updated.companyName || updated.productName} suspended.`);
      } catch (error) {
        notify(String((error as Error)?.message || 'Suspend failed.'), 'error');
      }
    },
    [applyServerConfig, notify],
  );

  const handlePriorityDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>, id: string) => {
    if (subscriberSort) setSubscriberSort(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
    setDraggingId(id);
    setDragOverId(id);
  }, [subscriberSort]);

  const handlePriorityDragOver = useCallback((event: React.DragEvent<HTMLTableRowElement>, targetId: string) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const sourceId = draggingId;
    if (!sourceId || sourceId === targetId) return;
    const target = event.currentTarget.getBoundingClientRect();
    const insertAfter = event.clientY >= target.top + target.height / 2;
    setConfigs((previous) => {
      const sourceIndex = previous.findIndex((config) => config.id === sourceId);
      if (sourceIndex < 0) return previous;
      const next = [...previous];
      const [moved] = next.splice(sourceIndex, 1);
      const targetIndex = next.findIndex((config) => config.id === targetId);
      if (targetIndex < 0) return previous;
      next.splice(targetIndex + (insertAfter ? 1 : 0), 0, moved);
      setDragOverId(targetId);
      return next;
    });
  }, [draggingId]);

  const handlePriorityDrop = useCallback(async (event: React.DragEvent<HTMLTableRowElement>) => {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData('text/plain') || draggingId;
    setDraggingId(null);
    setDragOverId(null);
    if (!sourceId) return;
    try {
      const savedOrder = await reorderWhiteLabelDeployments(configs.map((config) => config.id));
      const rankById = new Map(savedOrder.map((id, index) => [id, index + 1]));
      setConfigs((previous) => previous
        .map((config) => ({ ...config, priorityOrder: rankById.get(config.id) || config.priorityOrder }))
        .sort((a, b) => a.priorityOrder - b.priorityOrder));
      notify('Tenant priority order saved.');
    } catch (error) {
      notify(String((error as Error)?.message || 'Unable to save tenant priority order.'), 'error');
      void refresh();
    }
  }, [configs, draggingId, notify, refresh]);

  const handlePriorityDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDeleteConfig) return;
    setDeleting(true);
    try {
      await deleteWhiteLabelDeployment(confirmDeleteConfig.id);
      removeLocalConfig(confirmDeleteConfig.id);
      setConfirmDeleteConfig(null);
      notify('White-label deployment deleted.');
    } catch (error) {
      notify(String((error as Error)?.message || 'Delete failed.'), 'error');
      setConfirmDeleteConfig(null);
    } finally {
      setDeleting(false);
    }
  }, [confirmDeleteConfig, notify, removeLocalConfig]);

  const statusBadge = (config: WhiteLabelConfig) => {
    const base = 'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide';
    if (config.status === 'suspended') return `${base} bg-rose-500/15 text-rose-400`;
    if (config.status === 'active' && config.publishedAt) return `${base} bg-emerald-500/15 text-emerald-500`;
    if (config.publishError) return `${base} bg-rose-500/15 text-rose-400`;
    if (config.status === 'active') return `${base} bg-emerald-500/15 text-emerald-500`;
    return `${base} bg-amber-500/15 text-amber-500`;
  };

  const statusDot = (config: WhiteLabelConfig) => {
    if (config.status === 'suspended') return 'bg-rose-400';
    if (config.status === 'active' && config.publishedAt) return 'bg-emerald-400';
    if (config.publishError) return 'bg-rose-400';
    if (config.status === 'active') return 'bg-emerald-400';
    return 'bg-amber-400';
  };

  const statusLabel = (config: WhiteLabelConfig) => {
    if (config.status === 'suspended') return 'Suspended';
    // Standardize on the publish-lifecycle label: status 'active' is the single
    // "published/live" state (both Publish and the legacy Activate action set it),
    // so the badge must never switch between ACTIVE and LIVE based on publishedAt.
    if (config.status === 'active') {
      // A failed publish that never completed is still surfaced as an error.
      if (config.publishError && !config.publishedAt) return 'Publish Error';
      return 'Live';
    }
    if (config.publishError) return 'Publish Error';
    return 'Draft';
  };

  const sectionCardClass = isDark
    ? 'rounded-xl border border-slate-800/70 bg-[#11151c] p-5 shadow-[0_10px_24px_rgba(2,6,23,0.35)]'
    : 'rounded-xl border border-[#d6d6d6] bg-white p-5 shadow-[0_8px_20px_rgba(15,23,42,0.05)]';
  const subtle = isDark ? 'text-slate-400' : 'text-zinc-500';
  const inputClass = isDark
    ? 'mt-1 h-9 w-full rounded-xl border-slate-700 bg-slate-900/80 text-slate-100 placeholder:text-slate-500'
    : 'mt-1 h-9 w-full rounded-xl border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400';
  const selectClass = isDark
    ? 'mt-1 h-9 w-full rounded-xl border border-slate-700 bg-slate-900/80 pl-3 pr-10 text-sm text-slate-100'
    : 'mt-1 h-9 w-full rounded-xl border border-zinc-200 bg-white pl-3 pr-10 text-sm text-zinc-900';
  const modalPanelClass = isDark
    ? 'bg-[#0b0f14] text-white border border-slate-800/70'
    : 'bg-white text-zinc-900 border border-zinc-200';

  const metrics = [
    { label: 'Live', value: counts.active, className: 'text-emerald-500' },
    { label: 'Draft', value: counts.draft, className: 'text-amber-500' },
    { label: 'Suspended', value: counts.suspended, className: 'text-rose-400' },
    { label: 'Total', value: counts.total, className: 'text-blue-500' },
  ];

  const isLocalBuild = isLocalDevelopmentBuild();
  const generatedWebsiteUrl = buildProductionTenantWebsiteUrl(form.tenantSlug);
  const localHostUrl = buildLocalTenantWebsiteUrl(form.tenantSlug);
  // Optional enhancement: allow sorting the Active Subscribers column.
  const toggleSubscriberSort = useCallback(() => {
    setSubscriberSort((previous) => previous === 'asc' ? 'desc' : previous === 'desc' ? null : 'asc');
  }, []);
  const sortedConfigs = useMemo(() => {
    const keepTestLast = (left: WhiteLabelConfig, right: WhiteLabelConfig) => {
      const leftIsTest = left.tenantSlug === 'trade-proof-test';
      const rightIsTest = right.tenantSlug === 'trade-proof-test';
      if (leftIsTest !== rightIsTest) return leftIsTest ? 1 : -1;
      return 0;
    };
    if (!subscriberSort) return [...configs].sort((a, b) => keepTestLast(a, b));
    const sorted = [...configs];
    sorted.sort((a, b) => {
      const testOrder = keepTestLast(a, b);
      if (testOrder) return testOrder;
      const aa = Number(a.activeSubscribers || 0);
      const bb = Number(b.activeSubscribers || 0);
      return subscriberSort === 'asc' ? aa - bb : bb - aa;
    });
    return sorted;
  }, [configs, subscriberSort]);
  const tenantTotalPages = Math.max(1, Math.ceil(sortedConfigs.length / 10));
  const paginatedConfigs = useMemo(() => sortedConfigs.slice((tenantPage - 1) * 10, tenantPage * 10), [sortedConfigs, tenantPage]);
  useEffect(() => { if (tenantPage > tenantTotalPages) setTenantPage(tenantTotalPages); }, [tenantPage, tenantTotalPages]);

  const renderStatus = (config: WhiteLabelConfig) => (
    <span className={statusBadge(config)} data-testid={`status-${config.id}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusDot(config)}`} />
      {statusLabel(config)}
    </span>
  );

  const renderActions = (config: WhiteLabelConfig) => {
    const effectiveUrl = resolveEffectiveTenantWebsite(config);
    const iconBtnClass = `inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900'
    }`;
    return (
      <div className="flex flex-wrap items-center justify-end gap-0.5" data-testid={`actions-${config.id}`}>
        <Button
          type="button"
          variant="secondary"
          className={`${iconBtnClass} !p-0`}
          disabled={!effectiveUrl}
          title={effectiveUrl ? `Open ${effectiveUrl}` : 'No website address available for this tenant.'}
          aria-label={`Open website for ${config.tenantSlug}`}
          data-testid={`open-website-${config.id}`}
          onClick={() => openWebsiteInNewTab(resolveEffectiveTenantLoginUrl(config))}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="secondary" className={`${iconBtnClass} !p-0`} title="Edit" aria-label={`Edit tenant ${config.tenantSlug}`} data-testid={`edit-${config.id}`} onClick={() => openEdit(config)}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="secondary" className={`${iconBtnClass} !p-0`} title="Proposal PDF" aria-label={`Proposal PDF for ${config.tenantSlug}`} data-testid={`proposal-pdf-${config.id}`} onClick={() => { setProposalConfig(config); setProposalSummaryDraft(config.proposalFitSummary || ''); setOutreachFitSentenceDraft(config.outreachFitSentence || ''); setProposalEditing(false); setProposalError(null); }}>
          <FileText className="h-3.5 w-3.5" />
        </Button>
        {config.status !== 'active' ? (
          <Button
            type="button"
            variant="secondary"
            className={`${iconBtnClass} !p-0 ${isDark ? 'hover:text-emerald-300' : 'hover:text-emerald-600'}`}
            disabled={publishingId === config.id}
            title={publishingId === config.id ? 'Publishing…' : 'Publish Tenant'}
            aria-label={`Publish tenant ${config.tenantSlug}`}
            data-testid={`publish-${config.id}`}
            onClick={() => void handlePublish(config)}
          >
            {publishingId === config.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
          </Button>
        ) : null}
        {config.status === 'active' ? (
          <Button type="button" variant="secondary" className={`${iconBtnClass} !p-0`} title="Suspend" aria-label={`Suspend tenant ${config.tenantSlug}`} data-testid={`suspend-${config.id}`} onClick={() => void handleSuspend(config)}>
            <Ban className="h-3.5 w-3.5" />
          </Button>
        ) : null}
        {config.status !== 'active' ? (
          <Button type="button" variant="secondary" className={`${iconBtnClass} !p-0 ${isDark ? 'hover:text-rose-400' : 'hover:text-rose-600'}`} title="Delete" aria-label={`Delete tenant ${config.tenantSlug}`} data-testid={`delete-${config.id}`} onClick={() => setConfirmDeleteConfig(config)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
    );
  };

  const generatedAddressStatus = (config: WhiteLabelConfig): string => {
    if (isLocalBuild) return 'Local';
    if (!infrastructure) return 'Unknown';
    if (config.status === 'active' && config.publishedAt && infrastructure.status === 'configured') return 'Live';
    if (config.publishError) return 'Publish Error';
    if (config.status === 'active' && config.publishedAt && infrastructure.status !== 'configured') return 'Not Configured';
    return infrastructure.status === 'configured' ? 'Configured' : 'Not Configured';
  };

  const renderDomain = (config: WhiteLabelConfig) => {
    const generatedUrl = buildGeneratedTenantWebsiteUrl(config.tenantSlug);
    const localUrl = buildLocalTenantWebsiteUrl(config.tenantSlug);

    const generatedStatus = generatedAddressStatus(config);
    const customStatus = config.customDomain
      ? config.domainStatus === 'verified' ? 'Live'
        : config.domainStatus === 'pending' ? 'Pending DNS'
          : config.domainStatus === 'failed' ? 'Error'
            : 'Not Configured'
      : 'Not Configured';

    return (
      <div className="flex min-w-[200px] max-w-[320px] flex-col gap-1" data-testid={`domain-cell-${config.id}`}>
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="w-[4.2rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Generated</span>
          <a
            href={isLocalBuild && config.status !== 'active' ? localUrl : generatedUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={generatedUrl}
            data-testid={`website-link-${config.id}`}
            className="inline-flex min-w-0 flex-1 items-center gap-1 text-xs leading-tight text-sky-500 hover:underline"
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate">{buildGeneratedTenantDomain(config.tenantSlug)}</span>
          </a>
          <span className={`inline-flex shrink-0 rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${
            generatedStatus === 'Live' ? 'bg-emerald-500/15 text-emerald-500'
              : generatedStatus === 'Publish Error' ? 'bg-rose-500/15 text-rose-400'
                : generatedStatus === 'Local' ? 'bg-sky-500/15 text-sky-400'
                  : 'bg-slate-500/15 text-slate-400'
          }`} data-testid={`domain-badge-${config.id}`} data-generated-status={generatedStatus}>
            {generatedStatus}
          </span>
        </div>

        <div className="flex min-w-0 items-center gap-1.5">
          <span className="w-[4.2rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Custom</span>
          <span className="min-w-0 flex-1 truncate text-xs leading-tight text-slate-400 dark:text-slate-500">
            {config.customDomain ? buildCustomDomainWebsiteUrl(config.customDomain).replace(/^https:\/\//i, '') : 'Not configured'}
          </span>
          <span className={`inline-flex shrink-0 rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${
            customStatus === 'Live' ? 'bg-emerald-500/15 text-emerald-500'
              : customStatus === 'Pending DNS' ? 'bg-amber-500/15 text-amber-500'
                : customStatus === 'Error' ? 'bg-rose-500/15 text-rose-400'
                  : 'bg-slate-500/15 text-slate-400'
          }`} data-testid={`custom-status-${config.id}`}>
            {customStatus}
          </span>
        </div>
      </div>
    );
  };

  const proposalMissing = proposalConfig ? proposalMissingFields({
    ...proposalConfig,
    proposalFitSummary: proposalEditing || !proposalConfig.proposalFitSummary?.trim()
      ? proposalSummaryDraft
      : proposalConfig.proposalFitSummary,
  }) : [];

  const renderDate = (value: string | null | undefined) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <>
      <section className={`${sectionCardClass} relative`} data-testid="white-label-deployments-section">
        <div className="mb-3 flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <h2 className="text-xl font-semibold">WHITE-LABEL TENANTS</h2>
            <div className="flex items-center gap-x-5 gap-y-1 text-sm" data-testid="white-label-metrics">
              {metrics.map((metric, index) => (
                <div key={metric.label} className="flex items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{metric.label}</span>
                  <span className={`font-semibold leading-none ${metric.className}`} data-testid={`metric-${metric.label.toLowerCase()}`}>{metric.value}</span>
                  {index === 2 ? <span className={`mx-2 h-4 w-px ${isDark ? 'bg-slate-700' : 'bg-zinc-300'}`} /> : null}
                </div>
              ))}
            </div>
          </div>
          <Button type="button" className="h-10 border border-rose-500/30 bg-rose-500/10 text-rose-700 hover:bg-rose-500/15 dark:border-transparent dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90" onClick={openCreate} data-testid="add-tenant-btn">
            <Plus className="h-4 w-4" /> Add Tenant
          </Button>
        </div>

        {publishResult ? (
          <div className="relative mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="publish-result">
            <button type="button" className="absolute right-3 top-3 rounded border-[0.1px] border-emerald-500/30 bg-emerald-500/10 p-1 text-emerald-700 hover:bg-emerald-500/15 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100" aria-label="Close tenant published notice" onClick={() => setPublishResult(null)}>
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-emerald-500" />
              <h3 className="text-base font-semibold text-emerald-800 dark:text-emerald-100">Tenant published</h3>
            </div>
            <div className="mt-2 space-y-2">
              <p className="break-all font-mono text-sm text-emerald-800 dark:text-emerald-100" data-testid="publish-result-url">
                <span className="font-sans text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400/70">Live website: </span>
                {publishResult.effectiveUrl || publishResult.generatedUrl}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="h-9 rounded-lg border border-emerald-700/40 bg-white px-3 text-xs text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:bg-emerald-500/15"
                  data-testid="publish-open-website"
                  onClick={() => openWebsiteInNewTab(`${publishResult.effectiveUrl || publishResult.generatedUrl}/login`)}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open Website
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-9 rounded-lg border border-emerald-700/40 bg-white px-3 text-xs text-emerald-800 hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:bg-emerald-500/15"
                  data-testid="publish-copy-link"
                  onClick={() => { void copyTextToClipboard(publishResult.effectiveUrl || publishResult.generatedUrl).then(() => notify('Link copied.')); }}
                >
                  <Copy className="h-3.5 w-3.5" /> Copy Link
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {publishError ? (
          <div className="mt-4 flex items-center gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-100" data-testid="publish-error">
            <ShieldAlert className="h-4 w-4 shrink-0 text-rose-400" />
            <span className="text-rose-700 dark:text-rose-100">{publishError}</span>
          </div>
        ) : null}

        {tenantsExpanded ? <div className="mt-4">
          {loading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              <span>Loading white-label deployments…</span>
            </div>
          ) : loadError ? (
            <div className="flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400" />
              <span>{loadError}</span>
            </div>
          ) : configs.length === 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-5 text-center text-sm text-slate-400">
              No white-label tenants yet. Click <span className="font-semibold text-blue-400">Add Tenant</span> to start.
            </div>
          ) : (
            <div className={`overflow-x-auto rounded-2xl border ${isDark ? 'border-slate-800/70 bg-slate-950/40' : 'border-zinc-200 bg-white'}`}>
              <table className="w-full min-w-[820px] border-collapse text-left text-sm">
                <thead>
                  <tr className={isDark ? 'border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-500' : 'border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500'}>
                    <th className={`w-[5.5rem] border-r px-3 py-2 ${isDark ? 'border-slate-800' : 'border-zinc-200'}`}>Priority</th>
                    <th className="px-3 py-2">Brand / Company</th>
                    <th className="px-3 py-2">Assets</th>
                    <th className="px-3 py-2 text-center">Partner Share</th>
                    <th className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={toggleSubscriberSort}
                        className={`inline-flex items-center justify-center gap-1 transition-colors hover:text-zinc-700 dark:hover:text-slate-200 ${subscriberSort ? 'text-zinc-700 dark:text-slate-200' : ''}`}
                        aria-label="Sort by Active Subscribers"
                        title="Sort by Active Subscribers"
                      >
                        Active Subscribers
                        <span className="text-[10px]" aria-hidden="true">
                          {subscriberSort === 'asc' ? '↑' : subscriberSort === 'desc' ? '↓' : '↕'}
                        </span>
                      </button>
                    </th>
                    <th className="px-3 py-2">Domain Configuration</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-center">Updated</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedConfigs.map((config) => {
                    const visibleConfigIndex = sortedConfigs.findIndex((item) => item.id === config.id);
                    return (
                    <tr
                      key={config.id}
                      onDragOver={config.tenantSlug === 'trade-proof-test' ? undefined : (event) => handlePriorityDragOver(event, config.id)}
                      onDrop={config.tenantSlug === 'trade-proof-test' ? undefined : handlePriorityDrop}
                      className={`${config.tenantSlug === 'trade-proof-test' && isDark ? 'border-b border-slate-800/60 bg-[#380a0a]/90 last:border-b-0' : isDark ? 'border-b border-slate-800/60 last:border-b-0' : 'border-b border-zinc-200/70 bg-white text-zinc-900 last:border-b-0'} ${draggingId === config.id ? 'opacity-50' : ''} ${dragOverId === config.id && draggingId !== config.id ? (isDark ? 'border-t-2 border-blue-400' : 'border-t-2 border-blue-500') : ''}`}
                    >
                      <td className={`border-r px-3 py-2 ${isDark ? 'border-slate-800/70' : 'border-zinc-200/70'}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          {config.tenantSlug === 'trade-proof-test' ? <CheckCircle2 className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-zinc-500'}`} aria-label="TEST tenant" /> : <>
                          <button
                            type="button"
                            draggable={config.tenantSlug !== 'trade-proof-test'}
                            aria-label={`Drag to reorder ${config.companyName}`}
                            title="Drag to reorder"
                            data-testid={`priority-drag-handle-${config.id}`}
                            onDragStart={(event) => handlePriorityDragStart(event, config.id)}
                            onDragEnd={handlePriorityDragEnd}
                            className={`cursor-grab touch-none rounded p-1 active:cursor-grabbing ${isDark ? 'text-slate-500 hover:bg-slate-800 hover:text-slate-200' : 'text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700'}`}
                          >
                            <GripVertical className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <span className="font-mono text-xs font-semibold tabular-nums">#{visibleConfigIndex + 1}</span>
                          </>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {config.useTextLogo ? (
                            <div
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[9px] font-semibold leading-tight ${isDark ? 'bg-slate-800 text-slate-200' : 'bg-zinc-100 text-zinc-700'}`}
                              aria-label={`${config.companyName} text logo`}
                            >
                              {getTextLogoInitials(config.companyName)}
                            </div>
                          ) : (
                            <img
                              src={versionedBrandingUrl(config.compactLogoUrl || config.primaryLogoUrl, config.updatedAt) ?? getAppLogoSrc({ variant: 'symbol', theme: isDark ? 'dark' : 'light' })}
                              alt={`${config.companyName} icon logo`}
                              className="h-8 w-8 shrink-0 rounded-lg object-contain"
                            />
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-medium leading-tight">{config.companyName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs" title="Active assets">
                        {getActiveAssetAbbreviations(config).map((asset, index) => (
                          <span key={asset.code} className={asset.code === 'OP' ? 'text-slate-500' : undefined} title={asset.code === 'OP' ? 'Options — tracked for future support' : undefined}>
                            {index > 0 ? ' / ' : ''}{asset.code}
                          </span>
                        ))}
                      </td>
                      <td className={`px-3 py-2 text-center text-xs ${isDark ? 'text-slate-300' : 'text-zinc-700'}`}>
                        {Number(config.partnerRevenueShare ?? 35)}%
                      </td>
                      <td className={`px-3 py-2 text-center text-xs ${isDark ? 'text-slate-300' : 'text-zinc-700'}`}>{Number(config.activeSubscribers || 0)}</td>
                      <td className="px-3 py-2 text-xs">{renderDomain(config)}</td>
                      <td className="px-3 py-2 text-center">{renderStatus(config)}</td>
                      <td className={`px-3 py-2 text-center text-xs ${isDark ? 'text-slate-400' : 'text-zinc-600'}`}>{renderDate(config.updatedAt)}</td>
                      <td className="px-3 py-2 text-right">{renderActions(config)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className={`mt-3 grid gap-2 text-xs ${isDark ? 'text-slate-400' : 'text-zinc-500'} md:grid-cols-[1fr_auto_1fr] md:items-center`}>
            <span>Showing {sortedConfigs.length ? (tenantPage - 1) * 10 + 1 : 0}–{Math.min(tenantPage * 10, sortedConfigs.length)} of {sortedConfigs.length} tenants</span>
            <AdminTablePagination page={tenantPage} totalItems={sortedConfigs.length} onPageChange={setTenantPage} itemLabel="tenants" />
          </div>
        </div> : null}

        <button
          type="button"
          className={`absolute bottom-0 left-1/2 flex h-6 w-12 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-b-lg border transition-colors hover:shadow-[0_0_18px_rgba(59,130,246,0.35)] ${isDark ? 'border-slate-800/70 bg-slate-950 text-slate-400 hover:bg-[#071246] hover:text-slate-200' : 'border-zinc-200 bg-white text-zinc-500 hover:bg-white hover:text-zinc-800'}`}
          onClick={() => setTenantsExpanded((expanded) => !expanded)}
          aria-label={tenantsExpanded ? 'Collapse White Label Tenants' : 'Expand White Label Tenants'}
          aria-expanded={tenantsExpanded}
          data-testid="white-label-tenants-toggle"
        >
          {tenantsExpanded ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
        </button>
      </section>

      {modalOpen ? (
        <div className="white-label-modal-scroll fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto bg-black/60 p-4 md:p-8" onClick={() => { if (!saving) setModalOpen(false); }}>
          <div
            className={`w-full max-w-3xl rounded-[12px] shadow-2xl ${modalPanelClass}`}
            data-testid="white-label-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between rounded-[2px] border-b border-zinc-200 px-6 py-4 dark:border-slate-800">
              <div>
                <h3 className="text-xl font-semibold">{editingConfig ? 'Edit Tenant' : 'Add Tenant'}</h3>
                <p className={`mt-1 text-xs ${subtle}`}>Status: <span className="font-semibold text-amber-500">{form.status}</span>.</p>
              </div>
              <Button type="button" variant="secondary" className="h-9 w-9 rounded-xl p-0" aria-label="Close" onClick={() => { if (!saving) setModalOpen(false); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="white-label-modal-scroll max-h-[calc(100dvh-12rem)] space-y-8 overflow-y-auto px-6 py-6">
              {/* A. Company */}
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">A. Company</h4>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <label className="block text-sm font-medium">
                    Company Name *
                    <Input
                      aria-label="Company name"
                      data-field="companyName"
                      aria-invalid={formErrors.companyName ? true : undefined}
                      aria-describedby={formErrors.companyName ? 'companyName-error' : undefined}
                      className={`${inputClass} font-light ${formErrors.companyName ? 'border-rose-500' : ''}`}
                      value={form.companyName}
                      onChange={(event) => updateFormField('companyName', event.target.value)}
                    />
                    {formErrors.companyName ? <span id="companyName-error" className="mt-1 block text-xs text-rose-500">{formErrors.companyName}</span> : null}
                  </label>
                  <label className="block text-sm font-medium">
                    Tenant slug *
                    <Input
                      aria-label="Tenant slug"
                      data-field="tenantSlug"
                      aria-invalid={formErrors.tenantSlug ? true : undefined}
                      aria-describedby={formErrors.tenantSlug ? 'tenantSlug-error' : undefined}
                      className={`${inputClass} font-light ${formErrors.tenantSlug ? 'border-rose-500' : ''}`}
                      value={form.tenantSlug}
                      disabled={Boolean(editingConfig)}
                      onChange={(event) => updateFormField('tenantSlug', normalizeSlug(event.target.value))}
                    />
                    {formErrors.tenantSlug ? <span id="tenantSlug-error" className="mt-1 block text-xs text-rose-500">{formErrors.tenantSlug}</span> : null}
                    {editingConfig ? <span className="mt-1 block text-xs text-slate-400 dark:text-slate-500">Slugs are immutable after creation.</span> : null}
                  </label>
                  <div className="grid gap-3 md:col-span-2 md:grid-cols-2">
                    <label className="block text-sm font-medium">
                      Status
                      <div className="relative mt-1">
                        <select
                          aria-label="Status"
                          className={`${selectClass} mt-0 appearance-none pr-10 font-normal admin-tools-custom-chevron`}
                          value={form.status}
                          onChange={(event) => updateFormField('status', event.target.value)}
                        >
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                      </div>
                    </label>
                    <label className="block text-sm font-medium">
                      Onboarding
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-1 w-full"
                        disabled={!editingConfig || tenantOnboardingResetting}
                        onClick={() => { if (editingConfig) setConfirmTenantOnboardingReset(editingConfig); }}
                        data-testid="reset-tenant-onboarding"
                      >
                        {tenantOnboardingResetting ? 'Resetting…' : 'Reset'}
                      </Button>
                    </label>
                  </div>
                </div>
                {!editingConfig ? <label className="mt-4 flex items-start gap-3 text-sm font-medium"><input type="checkbox" checked={form.addToFoundingPartnerFunnel} onChange={(event) => updateFormField('addToFoundingPartnerFunnel', event.target.checked)} className="mt-0.5 h-4 w-4 rounded" /> <span>Add to Founding Partner Funnel<span className={`mt-1 block text-xs font-normal ${subtle}`}>Create a Founding Partner outreach record for this tenant.</span></span></label> : null}
              </section>

              {/* B. Brand assets */}
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">B. Brand assets</h4>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  PNG, JPG, WEBP or SVG up to 5 MB, max 2048px. Logos are stored in authenticated storage; the tenant record only keeps asset URLs.
                </p>
                {uploadError ? (
                  <div className="mt-4 flex items-center gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-100" role="alert" data-testid="logo-upload-error">
                    <ShieldAlert className="h-4 w-4 shrink-0 text-rose-400" />
                    <span className="min-w-0 flex-1 text-rose-700 dark:text-rose-100">{uploadError}</span>
                    <button
                      type="button"
                      className="ml-auto shrink-0 rounded p-1 text-rose-300 hover:bg-rose-500/20 hover:text-rose-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                      aria-label="Close logo upload error"
                      title="Close"
                      onClick={() => setUploadError(null)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
                <div className="mt-4 flex flex-wrap items-start gap-6 rounded-2xl border border-zinc-200 p-4 dark:border-slate-800">
                  {/* Each brand asset renders its Dark + Light variants together. */}
                  {([
                    {
                      label: 'Primary logo',
                      variants: [
                        { key: 'primaryLogo', variantLabel: 'Dark theme', urlField: 'primaryLogoUrl', addLabel: 'Add Logo', replaceLabel: 'Replace Logo' },
                        { key: 'primaryLogoLight', variantLabel: 'Light theme', urlField: 'primaryLogoLightUrl', addLabel: 'Add Logo (Light)', replaceLabel: 'Replace Logo (Light)' },
                      ],
                    },
                    {
                      label: 'Sidebar logo',
                      variants: [
                        { key: 'sidebarLogo', variantLabel: 'Dark theme', urlField: 'sidebarLogoUrl', addLabel: 'Add Sidebar Logo', replaceLabel: 'Replace Sidebar Logo' },
                        { key: 'sidebarLogoLight', variantLabel: 'Light theme', urlField: 'sidebarLogoLightUrl', addLabel: 'Add Sidebar Logo (Light)', replaceLabel: 'Replace Sidebar Logo (Light)' },
                      ],
                    },
                    {
                      label: 'Compact / logo mark',
                      variants: [
                        { key: 'compactLogo', variantLabel: 'Dark theme', urlField: 'compactLogoUrl', addLabel: 'Add Compact Logo', replaceLabel: 'Replace Compact Logo' },
                        { key: 'compactLogoLight', variantLabel: 'Light theme', urlField: 'compactLogoLightUrl', addLabel: 'Add Compact Logo (Light)', replaceLabel: 'Replace Compact Logo (Light)' },
                      ],
                    },
                  ] as const).map((group) => (
                    <div key={group.label} className="block w-full border-b border-zinc-200 py-4 first:pt-0 last:border-b-0 last:pb-0 dark:border-slate-800">
                      <p className="text-sm font-medium">{group.label}</p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {group.variants.map(({ key, variantLabel, urlField, addLabel, replaceLabel }) => (
                          <div key={key} className="flex w-full min-w-0 items-center gap-3 rounded-xl border border-zinc-200/70 p-2 dark:border-slate-800">
                            {form.useTextLogo ? (
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg text-center text-[9px] font-semibold leading-tight text-slate-700 dark:text-slate-200">
                                {key.toLowerCase().includes('compact') ? getTextLogoInitials(form.companyName) : key.toLowerCase().includes('sidebar') ? <span>{getTextLogoName(form.companyName).split(/\s+/).slice(0, 2).map((word) => <span key={word} className="block">{word}</span>)}</span> : getTextLogoName(form.companyName)}
                              </div>
                            ) : form[urlField] ? (
                              <img src={versionedBrandingUrl(form[urlField], editingConfig?.updatedAt) ?? form[urlField]} alt={variantLabel} className="h-10 w-10 rounded-lg object-contain" />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 text-[10px] font-bold text-zinc-300 dark:bg-slate-800 dark:text-slate-500">—</div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">{variantLabel}</p>
                                {form[urlField] ? (
                                  <button
                                    type="button"
                                    className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-rose-500 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-rose-400 dark:hover:text-rose-300"
                                    disabled={removingField !== null || uploadingField !== null}
                                    onClick={() => void removeLogo(urlField)}
                                    title="Remove logo"
                                  >
                                    {removingField === urlField ? 'REMOVING…' : 'REMOVE'}
                                  </button>
                                ) : null}
                              </div>
                              <label className="mt-1 block cursor-pointer">
                                <span className="inline-flex h-8 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-zinc-300 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900">
                                  {uploadingField === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                                  {uploadingField === key ? 'Uploading…' : form[urlField] ? replaceLabel : addLabel}
                                </span>
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                  className="sr-only"
                                  disabled={uploadingField !== null}
                                  onChange={(event) => {
                                    void handleLogoFileChange(key, event.target.files?.[0]);
                                    event.target.value = '';
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="grid w-full grid-cols-2 gap-3">
                    {([
                      { label: 'Loading page logo', key: 'loadingPageLogo', variantLabel: 'Single asset', urlField: 'loadingPageLogoUrl', addLabel: 'Add Loading Page Logo', replaceLabel: 'Replace Loading Page Logo' },
                      { label: 'Favicon', key: 'favicon', variantLabel: 'Single asset', urlField: 'faviconUrl', addLabel: 'Add Favicon', replaceLabel: 'Replace Favicon' },
                    ] as const).map(({ label, key, variantLabel, urlField, addLabel, replaceLabel }) => (
                      <div key={label} className="w-full min-w-0">
                        <p className="text-sm font-medium">{label}</p>
                        <div className="mt-3">
                          <div className="flex w-full min-w-0 items-center gap-3 rounded-xl border border-zinc-200/70 p-2 dark:border-slate-800">
                            {form.useTextLogo && key === 'loadingPageLogo' ? (
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg text-center text-[9px] font-semibold leading-tight text-slate-700 dark:text-slate-200">{getTextLogoName(form.companyName)}</div>
                            ) : form[urlField] ? (
                              <img src={versionedBrandingUrl(form[urlField], editingConfig?.updatedAt) ?? form[urlField]} alt={variantLabel} className="h-10 w-10 rounded-lg object-contain" />
                            ) : (
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-zinc-100 text-[10px] font-bold text-zinc-300 dark:bg-slate-800 dark:text-slate-500">—</div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500">{variantLabel}</p>
                                {form[urlField] ? (
                                  <button
                                    type="button"
                                    className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-rose-500 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-rose-400 dark:hover:text-rose-300"
                                    disabled={removingField !== null || uploadingField !== null}
                                    onClick={() => void removeLogo(urlField)}
                                    title="Remove logo"
                                  >
                                    {removingField === urlField ? 'REMOVING…' : 'REMOVE'}
                                  </button>
                                ) : null}
                              </div>
                              <label className="mt-1 block cursor-pointer">
                                <span className="inline-flex h-8 w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-zinc-300 text-xs font-medium text-zinc-600 hover:bg-zinc-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900">
                                  {uploadingField === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                                  {uploadingField === key ? 'Uploading…' : form[urlField] ? replaceLabel : addLabel}
                                </span>
                                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="sr-only" disabled={uploadingField !== null} onChange={(event) => { void handleLogoFileChange(key, event.target.files?.[0]); event.target.value = ''; }} />
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-xs font-medium">
                      <span className="text-slate-400 dark:text-slate-500">Size</span>
                      <select
                        aria-label="Size"
                        className="admin-tools-select h-8 w-28 appearance-none rounded-lg border border-zinc-300 bg-white pl-2 pr-8 text-xs font-medium text-zinc-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                        value={form.loadingPageLogoSize}
                        onChange={(event) => updateFormField('loadingPageLogoSize', event.target.value as WhiteLabelFormState['loadingPageLogoSize'])}
                      >
                        <option className="bg-white text-zinc-900 dark:bg-slate-900 dark:text-slate-100" value="small">Small</option>
                        <option className="bg-white text-zinc-900 dark:bg-slate-900 dark:text-slate-100" value="medium">Medium</option>
                        <option className="bg-white text-zinc-900 dark:bg-slate-900 dark:text-slate-100" value="large">Large</option>
                        <option className="bg-white text-zinc-900 dark:bg-slate-900 dark:text-slate-100" value="x-large">X-Large</option>
                      </select>
                    </label>
                    <label className="inline-flex h-8 items-center gap-2 rounded-lg border border-zinc-300 px-2 text-xs font-medium text-slate-400 dark:border-slate-700 dark:text-slate-500">
                      <span>Glow</span>
                      <span className="inline-flex items-center gap-2 text-xs font-medium">
                        <button
                          type="button"
                          role="switch"
                          aria-label="Glow"
                          aria-checked={form.loadingPageLogoGlow}
                          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-0 p-0 outline-none focus:outline-none focus-visible:ring-0 ${form.loadingPageLogoGlow ? 'bg-red-600' : 'bg-slate-700'}`}
                          onClick={() => updateFormField('loadingPageLogoGlow', !form.loadingPageLogoGlow)}
                        >
                          <span className={`absolute left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${form.loadingPageLogoGlow ? 'translate-x-4' : 'translate-x-0'}`} aria-hidden="true" />
                        </button>
                        <span className={form.loadingPageLogoGlow ? 'text-blue-300' : 'text-slate-400'}>{form.loadingPageLogoGlow ? 'ON' : 'OFF'}</span>
                      </span>
                    </label>
                    <div className="flex min-w-[18rem] flex-1 items-center justify-between gap-4 rounded-xl border border-zinc-200/70 p-3 dark:border-slate-800">
                      <div>
                      <p className="text-sm font-medium">Text Logo</p>
                      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Use the company name and initials instead of uploaded logo images.</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-label="Text Logo"
                        aria-checked={form.useTextLogo}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-0 p-0 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${form.useTextLogo ? 'bg-blue-600' : 'bg-slate-700'}`}
                        onClick={() => updateFormField('useTextLogo', !form.useTextLogo)}
                      >
                        <span className={`absolute left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${form.useTextLogo ? 'translate-x-4' : 'translate-x-0'}`} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </section>
              {/* C. Brand appearance */}
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">C. Brand appearance</h4>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Accent colors reuse the existing theme system via a single CSS variable.</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <label className="block text-sm font-medium">
                    Primary brand color
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="color"
                        aria-label="Primary brand color"
                        className="h-10 w-14 cursor-pointer rounded-xl border border-zinc-300 bg-transparent p-0 dark:border-slate-700 [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
                        value={/^#[0-9a-fA-F]{6}$/.test(form.primaryColor) ? form.primaryColor : '#2563eb'}
                        onChange={(event) => updateFormField('primaryColor', event.target.value)}
                      />
                      <Input aria-label="Primary brand color hex" className={`${inputClass} font-light`} value={form.primaryColor} onChange={(event) => updateFormField('primaryColor', event.target.value)} />
                    </div>
                  </label>
                  <label className="block text-sm font-medium">
                    Secondary color (optional)
                    <div className="mt-1 flex items-center gap-3">
                      <input
                        type="color"
                        aria-label="Secondary color"
                        className="h-10 w-14 cursor-pointer rounded-xl border border-zinc-300 bg-transparent p-0 dark:border-slate-700 [&::-moz-color-swatch]:border-0 [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
                        value={/^#[0-9a-fA-F]{6}$/.test(form.secondaryColor) ? form.secondaryColor : '#0ea5e9'}
                        onChange={(event) => updateFormField('secondaryColor', event.target.value)}
                      />
                      <Input aria-label="Secondary color hex" className={`${inputClass} font-light`} value={form.secondaryColor} onChange={(event) => updateFormField('secondaryColor', event.target.value)} />
                    </div>
                  </label>
                  <label className="block text-sm font-medium">
                    Partner Revenue Share (%)
                    <Input
                      aria-label="Partner Revenue Share"
                      data-field="partnerRevenueShare"
                      aria-invalid={formErrors.partnerRevenueShare ? true : undefined}
                      aria-describedby={formErrors.partnerRevenueShare ? 'partnerRevenueShare-error' : undefined}
                      inputMode="decimal"
                      className={`${inputClass} font-light ${formErrors.partnerRevenueShare ? 'border-rose-500' : ''}`}
                      value={form.partnerRevenueShare}
                      onChange={(event) => updateFormField('partnerRevenueShare', event.target.value.replace(/[^0-9.]/g, ''))}
                    />
                    <span className="mt-1 block text-xs font-normal text-slate-400 dark:text-slate-500">
                      The partner&apos;s share of monthly recurring revenue. Defaults to 35%.
                    </span>
                    {formErrors.partnerRevenueShare ? <span id="partnerRevenueShare-error" className="mt-1 block text-xs text-rose-500">{formErrors.partnerRevenueShare}</span> : null}
                  </label>
                  <label className="block text-sm font-medium">
                    Partner Monthly Price ($)
                    <Input
                      aria-label="Partner Monthly Price"
                      data-field="partnerMonthlyPrice"
                      aria-invalid={formErrors.partnerMonthlyPrice ? true : undefined}
                      aria-describedby={formErrors.partnerMonthlyPrice ? 'partnerMonthlyPrice-error' : undefined}
                      inputMode="decimal"
                      className={`${inputClass} font-light ${formErrors.partnerMonthlyPrice ? 'border-rose-500' : ''}`}
                      value={form.partnerMonthlyPrice}
                      onChange={(event) => updateFormField('partnerMonthlyPrice', event.target.value.replace(/[^0-9.]/g, ''))}
                    />
                    <span className="mt-1 block text-xs font-normal text-slate-400 dark:text-slate-500">
                      Monthly price per subscriber, used to compute Total MRR and payouts. Defaults to $45.
                    </span>
                    {formErrors.partnerMonthlyPrice ? <span id="partnerMonthlyPrice-error" className="mt-1 block text-xs text-rose-500">{formErrors.partnerMonthlyPrice}</span> : null}
                  </label>
                  <label className="block text-sm font-medium md:col-span-2">
                    Proposal Fit Summary
                    <textarea
                      aria-label="Proposal Fit Summary"
                      data-field="proposalFitSummary"
                      rows={3}
                      className={`${inputClass} h-auto py-2 pl-6 font-light`}
                      placeholder="Approved 2–4 sentence explanation of why Trade-Proof fits this community."
                      value={form.proposalFitSummary}
                      onChange={(event) => updateFormField('proposalFitSummary', event.target.value)}
                    />
                    <span className="mt-1 block text-xs font-normal text-slate-400 dark:text-slate-500">Reused in generated proposals; this is approved sales copy, not AI-generated text.</span>
                  </label>
                </div>
              </section>

              {/* D. Website Address */}
              <section data-testid="website-address-section">
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">Website Address</h4>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  The generated address derives from the tenant slug and always remains available as the demo and
                  fallback URL. Optionally connect a customer-owned domain.
                </p>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-zinc-200 p-4 text-sm dark:border-slate-800">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Generated Website</p>
                    <Input
                      aria-label="Generated website address"
                      readOnly
                      data-testid="generated-website-url"
                      className={`${inputClass} mt-2 font-mono`}
                      value={buildTenantLoginUrl(form.tenantSlug)}
                    />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[11px] text-slate-500">Demo / fallback · always available</span>
                      <Button type="button" variant="secondary" className="h-8 rounded-lg px-2.5 text-xs" disabled={!generatedWebsiteUrl} data-testid="open-generated-website" onClick={() => openWebsiteInNewTab(buildTenantLoginUrl(form.tenantSlug))}>
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-zinc-200 p-4 text-sm dark:border-slate-800" data-testid="custom-domain-panel">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Custom Domain</p>
                    {!editingConfig ? (
                      <p className="mt-3 rounded-xl border border-slate-700 bg-slate-900/40 px-3 py-2 text-xs text-slate-400">
                        Save this tenant as a draft first, then reopen it to connect a custom domain.
                      </p>
                    ) : editingConfig.customDomain ? (
                      <>
                        <p className="mt-2 break-all font-mono text-sm text-sky-500">{editingConfig.customDomain}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-500" data-testid="custom-domain-status">
                            {customDomainStatusLabel(editingConfig)}
                          </span>
                          {domainBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" /> : null}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-8 rounded-lg px-3 text-xs"
                            disabled={domainBusy}
                            data-testid="check-domain-btn"
                            onClick={() => void handleVerifyDomain()}
                          >
                            {domainBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            Check Verification
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-8 rounded-lg px-3 text-xs text-rose-400"
                            disabled={domainBusy}
                            data-testid="disconnect-domain-btn"
                            onClick={() => void handleDisconnectDomain()}
                          >
                            <X className="h-3.5 w-3.5" /> Disconnect Domain
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Input
                          aria-label="Custom domain"
                          data-testid="custom-domain-input"
                          className={`${inputClass} mt-2 font-mono`}
                          placeholder="journal.customer.com"
                          value={customDomainDraft}
                          disabled={domainBusy}
                          onChange={(event) => {
                            setCustomDomainDraft(event.target.value);
                            setDomainError(null);
                            setDomainNotice(null);
                          }}
                        />
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-8 rounded-lg px-3 text-xs"
                            disabled={domainBusy || !customDomainDraft.trim()}
                            data-testid="connect-domain-btn"
                            onClick={() => void handleConnectDomain()}
                          >
                            {domainBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                            Connect Domain
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {editingConfig?.customDomain && editingConfig.dnsInstructions?.length ? (
                  <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="dns-instructions">
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">DNS record required</p>
                    <p className="mt-1 text-xs text-amber-100">
                      Domain: <span className="font-mono">{editingConfig.customDomain}</span>
                    </p>
                    <div className="mt-2 space-y-2">
                      {editingConfig.dnsInstructions.map((record, index) => (
                        <div key={`${record.type}-${index}`} className="rounded-lg bg-black/20 px-3 py-2 text-[11px]">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="text-amber-200/80">Type: <span className="font-mono text-amber-100">{record.type}</span></span>
                            <span className="text-amber-200/80">Name: <span className="font-mono text-amber-100">{record.domain || editingConfig.customDomain}</span></span>
                            <span className="text-amber-200/80">Value: <span className="break-all font-mono text-amber-100">{record.value}</span></span>
                            <Button
                              type="button"
                              variant="secondary"
                              className="ml-auto h-7 rounded-lg px-2 text-[10px]"
                              data-testid={`copy-dns-value-${index}`}
                              onClick={() => { void copyTextToClipboard(record.value).then((ok) => setCopiedLabel(ok ? `dns-${index}` : null)); }}
                            >
                              <Copy className="h-3 w-3" /> {copiedLabel === `dns-${index}` ? 'Copied' : 'Copy Value'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-amber-200/70">
                      Add this DNS record at the provider where your domain is managed. Then return here and click Check Verification.
                    </p>
                  </div>
                ) : null}

                {editingConfig?.customDomain && editingConfig.domainStatus === 'verified' ? (
                  <div className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="custom-domain-verified">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">Verified</p>
                    <p className="mt-1 font-mono text-sm text-emerald-100">{editingConfig.customDomain}</p>
                    <p className="mt-1 text-[11px] text-emerald-200/70">
                      The generated fallback <span className="font-mono">{generatedWebsiteUrl}</span> remains available.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button type="button" variant="secondary" className="h-8 rounded-lg px-2.5 text-xs" data-testid="verified-open-website"
                        onClick={() => openWebsiteInNewTab(buildCustomDomainLoginUrl(editingConfig.customDomain ?? ''))}>
                        <ExternalLink className="h-3.5 w-3.5" /> Open Website
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-zinc-200 px-3 py-2 text-xs dark:border-slate-800">
                  <span className="text-slate-400">Local Host</span>
                  <span className="font-mono text-sky-500" data-testid="local-host-url">{localHostUrl || '—'}</span>
                  <Button type="button" variant="secondary" className="h-7 rounded-lg px-2 text-[10px]" disabled={!localHostUrl} data-testid="open-local-host" onClick={() => openWebsiteInNewTab(localHostUrl)}>
                    <ExternalLink className="h-3 w-3" /> Open
                  </Button>
                </div>

                {domainError ? (
                  <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200" data-testid="domain-error">
                    {domainError}
                  </div>
                ) : null}
                {domainNotice ? (
                  <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100" data-testid="domain-notice">
                    {domainNotice}
                  </div>
                ) : null}
              </section>

              {/* E. Wording */}
              <section>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">E. Wording</h4>
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Centralized tokens with Trade-Proof defaults. No global string replacement.</p>
                  </div>
                  <Button type="button" variant="secondary" className="h-8 rounded-lg px-3 text-xs" onClick={regenerateWording}>
                    Regenerate defaults
                  </Button>
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {WHITE_LABEL_WORDING_TOKEN_KEYS.map((token) => (
                    <label key={token} className="block text-sm font-semibold">
                      {WORDING_LABELS[token] || token}
                      <textarea
                        aria-label={token}
                        rows={2}
                        className={`mt-1 h-auto min-h-12 w-full rounded-none border px-2 text-sm font-normal ${
                          isDark
                            ? 'border-slate-700 bg-slate-900/80 text-slate-400 placeholder:text-slate-500'
                            : 'border-zinc-200 bg-white text-zinc-500 placeholder:text-zinc-400'
                        }`}
                        value={form.wording[token] || ''}
                        onChange={(event) => updateWordingToken(token, event.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </section>

              {/* F. Feature configuration */}
              <section>
                <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-500">F. Feature configuration</h4>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                  A new white-label tenant inherits deployment defaults unless changed explicitly.
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {([
                    { key: 'stocks', label: 'Stocks' },
                    { key: 'futures', label: 'Futures' },
                    { key: 'forex', label: 'Forex' },
                    { key: 'crypto', label: 'Crypto' },
                    { key: 'replayChart', label: 'Replay Chart' },
                  ] as const).map(({ key, label }) => (
                    <label key={key} className="flex cursor-pointer items-center justify-between rounded-2xl border border-zinc-200 px-4 py-3 text-sm dark:border-slate-800">
                      <span>{label}</span>
                      <input
                        type="checkbox"
                        aria-label={label}
                        checked={Boolean(form.launchControls[key])}
                        onChange={(event) => updateLaunchControl(key, event.target.checked)}
                      />
                    </label>
                  ))}
                  <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-zinc-200 px-4 py-3 text-sm dark:border-slate-800">
                    <span>
                      <span className="block">Options</span>
                      <span className="mt-1 block text-xs font-normal text-zinc-500 dark:text-slate-500">Tracked for future support — Options is not currently available in Trade-Proof.</span>
                    </span>
                    <input type="checkbox" aria-label="Options" checked={form.optionsRelevant} onChange={(event) => setForm((previous) => ({ ...previous, optionsRelevant: event.target.checked }))} />
                  </label>
                  <div className="grid gap-3 md:grid-cols-2 md:col-span-2">
                  <label className="block text-sm font-medium">
                    Navigation layout
                    <select
                      aria-label="Navigation layout"
                      className={`${selectClass} font-normal`}
                      value={form.launchControls.navigationLayout}
                      onChange={(event) => updateLaunchControl('navigationLayout', event.target.value as 'both' | 'side_only' | 'top_only')}
                    >
                      <option value="both">Top + side</option>
                      <option value="top_only">Top only</option>
                      <option value="side_only">Side only</option>
                    </select>
                  </label>
                  <label className="block text-sm font-medium">
                    Theme
                    <select aria-label="Theme" className={`${selectClass} font-normal`} value={form.launchControls.themeMode} onChange={(event) => updateLaunchControl('themeMode', event.target.value as any)}>
                      <option value="light_only">Light only</option>
                      <option value="dark_only">Dark only</option>
                      <option value="light_dark">Light + Dark</option>
                    </select>
                  </label>
                  </div>
                </div>
              </section>

              {formError ? (
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{formError}</div>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-3 rounded-[2px] border-t border-zinc-200 px-6 py-4 dark:border-slate-800">
              <Button type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
              {editingConfig ? (
                <Button type="button" onClick={() => void handleSaveDraft()} disabled={saving || Boolean(uploadingField)} data-testid="save-white-label-draft">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save changes
                </Button>
              ) : (
                <>
                  <Button type="button" variant="secondary" onClick={() => void handleSaveDraft()} disabled={saving || Boolean(uploadingField)} data-testid="save-white-label-draft">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save as Draft
                  </Button>
                  <Button type="button" onClick={() => void handleSaveDraft({ applyStatus: form.status })} disabled={saving || Boolean(uploadingField)} data-testid="create-tenant-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Create Tenant
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {proposalConfig ? (
        <div className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 p-4" onClick={() => { if (!proposalLoading) setProposalConfig(null); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="proposal-title" className={`w-full max-w-md rounded-2xl border p-6 shadow-2xl ${isDark ? 'border-slate-800 bg-[#0b0f14] text-white' : 'border-zinc-200 bg-white text-zinc-900'}`} onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 id="proposal-title" className="text-lg font-semibold">{proposalConfig.companyName} Proposal</h3>
                <p className={`mt-2 text-sm ${subtle}`}>Generate the latest proposal using {proposalConfig.companyName}&apos;s current company and commercial details.</p>
              </div>
              <Button type="button" variant="secondary" className="h-8 w-8 shrink-0 rounded-lg p-0" aria-label="Close" disabled={Boolean(proposalLoading)} onClick={() => setProposalConfig(null)}><X className="h-4 w-4" /></Button>
            </div>
            {!proposalConfig.proposalFitSummary?.trim() || proposalEditing ? (
              <div className="mt-5">
                <label htmlFor="proposal-fit-summary" className="text-sm font-semibold">Proposal Fit Summary</label>
                <textarea id="proposal-fit-summary" data-testid="proposal-fit-summary" rows={5} maxLength={600} autoFocus={!proposalConfig.proposalFitSummary?.trim()} value={proposalSummaryDraft} onChange={(event) => { setProposalSummaryDraft(event.target.value); setProposalError(null); }} className={`${inputClass} h-auto py-2 pl-6`} placeholder="Add a short 2–4 sentence explanation of why Trade-Proof fits this company and its community." />
                <p className={`mt-1 text-xs ${proposalSummaryDraft.length > 600 ? 'text-rose-500' : subtle}`}>Add a short 2–4 sentence explanation of why Trade-Proof fits this company and its community. This will appear in the proposal and be saved for future use.</p>
              </div>
            ) : null}
            <div className="mt-5">
              <label htmlFor="outreach-fit-sentence" className="text-sm font-semibold">Outreach Fit Sentence</label>
              <textarea id="outreach-fit-sentence" data-testid="outreach-fit-sentence" rows={2} maxLength={300} value={outreachFitSentenceDraft} onChange={(event) => { setOutreachFitSentenceDraft(event.target.value); setProposalError(null); }} className={`${inputClass} h-auto py-2 pl-6`} placeholder="I thought this partner could be a strong fit because..." />
              <p className={`mt-1 text-xs ${outreachFitSentenceDraft.length > 300 ? 'text-rose-500' : subtle}`}>One natural sentence explaining why this partner/community could be a strong fit. Used in the outreach email only.</p>
            </div>
            {proposalMissing.length ? <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-200"><p className="font-semibold">Proposal incomplete</p><p className="mt-1">Missing: {proposalMissing.join(' · ')}</p><p className="mt-1 text-xs opacity-80">Complete these fields before generating the proposal.</p></div> : null}
            {proposalError ? <div className="mt-4 whitespace-pre-line rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600 dark:text-rose-300">{proposalError}</div> : null}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="secondary" className="font-normal" disabled={Boolean(proposalLoading)} onClick={() => setProposalConfig(null)}>Cancel</Button>
              {proposalConfig.proposalFitSummary && !proposalEditing ? <button type="button" className="bg-[#1f2937] px-2 text-xs text-[#3bf77d] hover:underline" disabled={Boolean(proposalLoading)} onClick={() => { setProposalSummaryDraft(proposalConfig.proposalFitSummary || ''); setProposalEditing(true); setProposalError(null); }}>Edit Fit Summary</button> : null}
              <Button type="button" variant="secondary" className="font-normal" disabled={Boolean(proposalLoading) || proposalMissing.filter((field) => field !== 'Proposal Fit Summary').length > 0 || (proposalEditing && !proposalSummaryDraft.trim())} onClick={() => void handleProposal('open')}>{proposalSaving ? 'Saving…' : proposalLoading === 'open' ? 'Generating proposal…' : proposalConfig.proposalFitSummary && !proposalEditing ? 'Open Proposal' : 'Save & Open Proposal'}</Button>
              <Button type="button" disabled={Boolean(proposalLoading) || proposalMissing.filter((field) => field !== 'Proposal Fit Summary').length > 0 || (proposalEditing && !proposalSummaryDraft.trim())} onClick={() => void handleProposal('download')}>{proposalSaving ? 'Saving…' : proposalLoading === 'download' ? 'Generating proposal…' : proposalConfig.proposalFitSummary && !proposalEditing ? 'Download PDF' : 'Save & Download PDF'}</Button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={Boolean(confirmTenantOnboardingReset)}
        onClose={() => { if (!tenantOnboardingResetting) setConfirmTenantOnboardingReset(null); }}
        onConfirm={() => void handleTenantOnboardingReset()}
        title="Reset onboarding for this tenant?"
        message={`All users in ${confirmTenantOnboardingReset?.companyName || confirmTenantOnboardingReset?.productName || 'this tenant'} will see the onboarding tour again the next time they are eligible to launch it.`}
        confirmText={tenantOnboardingResetting ? 'Resetting…' : 'Reset onboarding'}
        confirmDisabled={tenantOnboardingResetting}
      />

      <ConfirmationModal
        isOpen={Boolean(confirmDeleteConfig)}
        onClose={() => setConfirmDeleteConfig(null)}
        onConfirm={() => void handleConfirmDelete()}
        title="Delete Tenant?"
        message={confirmDeleteConfig
          ? `Tenant: ${confirmDeleteConfig.companyName || confirmDeleteConfig.productName}\nSlug: ${confirmDeleteConfig.tenantSlug}\n\nDeleting removes the tenant configuration and stops access to its generated and custom domain addresses. This cannot be undone.`
          : 'Delete this white-label tenant?'}
        confirmText={deleting ? 'Deleting…' : 'Delete Tenant'}
        cancelText="Cancel"
      />
    </>
  );
}
